import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ChevronDown, ChevronRight, KeyRound, Plus, RotateCcw, Search } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import { AdminApiKey, ApiKeyUsage, adminCreateApiKey, adminRestoreApiKey, adminRevokeApiKey, getAllApiKeys, getApiKeyUsage } from "interfaces/apiKeys";
import { searchApiUsers } from "interfaces/publicApi";
import { ActivityEntry, getActivityFeed } from "interfaces/activity";

type Status = "all" | "active" | "revoked";

function formatDate(d?: string) {
    if (!d) return "—";
    const date = new Date(d);
    const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins}min`;
    if (diffMins < 1440) return `Il y a ${Math.floor(diffMins / 60)}h`;
    if (diffMins < 7 * 1440) return `Il y a ${Math.floor(diffMins / 1440)}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

function UsagePanel({ apiKey }: { apiKey: AdminApiKey }) {
    const [usage, setUsage] = useState<ApiKeyUsage | null>(null);
    const [calls, setCalls] = useState<ActivityEntry[]>([]);

    useEffect(() => {
        getApiKeyUsage(apiKey._id).then(setUsage).catch(() => setUsage(null));
        getActivityFeed({ apiKeyId: apiKey._id, limit: 30 }).then((r) => setCalls(r.entries)).catch(() => setCalls([]));
    }, [apiKey._id]);

    if (!usage) return <div className="px-4 py-6 text-xs text-gray-400">Chargement…</div>;

    const maxDay = Math.max(1, ...usage.daily.map((d) => d.calls));

    return (
        <div className="px-4 py-4 bg-[#F9F6F3] border-t border-gray-100 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-3">
                <p className="text-xs font-medium text-gray-600 mb-2">Appels par jour ({usage.days} derniers jours)</p>
                {usage.daily.length === 0 ? (
                    <p className="text-xs text-gray-400">Aucun appel enregistré sur la période.</p>
                ) : (
                    <div className="flex items-end gap-1 h-16">
                        {usage.daily.map((d) => (
                            <div key={d.day} className="flex-1 min-w-[4px] bg-[#1A1A1A] rounded-sm" style={{ height: `${(d.calls / maxDay) * 100}%` }} title={`${d.day} : ${d.calls} appels`} />
                        ))}
                    </div>
                )}
            </div>

            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg">
                <p className="text-xs font-medium text-gray-600 px-3 py-2 border-b border-gray-100">Routes appelées</p>
                {usage.routes.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-3">—</p>
                ) : (
                    <table className="w-full">
                        <tbody>
                            {usage.routes.map((r, i) => (
                                <tr key={i} className="border-b border-gray-50 last:border-0">
                                    <td className="px-3 py-1.5 text-xs font-mono text-gray-800">
                                        <span className="text-gray-400 mr-1">{r.method}</span>
                                        {r.route}
                                    </td>
                                    <td className="px-3 py-1.5 text-xs text-right text-gray-900">{r.calls}</td>
                                    <td className="px-3 py-1.5 text-xs text-right">{r.errors > 0 ? <span className="text-red-600">{r.errors} err.</span> : null}</td>
                                    <td className="px-3 py-1.5 text-xs text-right text-gray-500">{formatDate(r.lastAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg">
                <p className="text-xs font-medium text-gray-600 px-3 py-2 border-b border-gray-100">Adresses IP</p>
                {usage.ips.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-3">—</p>
                ) : (
                    <ul>
                        {usage.ips.map((ip, i) => (
                            <li key={i} className="flex justify-between px-3 py-1.5 text-xs border-b border-gray-50 last:border-0">
                                <span className="font-mono text-gray-800">{ip.ip || "?"}</span>
                                <span className="text-gray-500">{ip.calls} · {formatDate(ip.lastAt)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-600">Derniers appels</p>
                    {apiKey.user && (
                        <Link to={`/bo/activity?user=${apiKey.user._id}`} className="text-xs text-gray-500 hover:text-gray-900 underline">
                            Toute l'activité de l'utilisateur
                        </Link>
                    )}
                </div>
                {calls.length === 0 ? (
                    <p className="text-xs text-gray-400 px-3 py-3">—</p>
                ) : (
                    <ul>
                        {calls.map((c) => (
                            <li key={c._id} className="flex items-center gap-3 px-3 py-1.5 text-xs border-b border-gray-50 last:border-0">
                                <span className="text-gray-400 w-24 shrink-0">{formatDate(c.createdAt)}</span>
                                <span className={`w-10 shrink-0 ${c.statusCode && c.statusCode >= 400 ? "text-red-600" : "text-gray-500"}`}>{c.statusCode}</span>
                                <span className="font-mono text-gray-800 truncate" title={c.url}>
                                    <span className="text-gray-400 mr-1">{c.method}</span>
                                    {c.url}
                                </span>
                                <span className="ml-auto text-gray-400 shrink-0">{c.ip}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

type FoundUser = Awaited<ReturnType<typeof searchApiUsers>>["users"][number];

async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // navigator.clipboard is unavailable outside secure contexts (plain http)
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(el);
        return ok;
    }
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const requestError = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;

/**
 * The only place keys are created: pick a user (or type their exact e-mail), name the key, create. The
 * secret is shown once here (the client can also show / copy it in Settings → Accès API, which appears
 * once they have a key). API access is on by default. Other BO pages link here with ?userId=…&email=…
 * to preselect a user.
 */
function CreateKeyPanel({ onCreated }: { onCreated: () => void }) {
    const [params] = useSearchParams();
    const [q, setQ] = useState("");
    const [found, setFound] = useState<FoundUser[]>([]);
    const [searchedFor, setSearchedFor] = useState(""); // the text `found` / `searchError` answer
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [selected, setSelected] = useState<{ _id: string; email: string } | null>(() => {
        const id = params.get("userId");
        return id ? { _id: id, email: params.get("email") || id } : null;
    });
    const [name, setName] = useState("API");
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState<{ secret: string; email: string; userId: string; name: string } | null>(null);
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const text = q.trim();

    useEffect(() => {
        if (text.length < 2) {
            setFound([]);
            setSearchedFor("");
            setSearchError(null);
            setSearching(false);
            return;
        }
        let stale = false;
        setSearching(true);
        const t = setTimeout(() => {
            searchApiUsers(text)
                .then((r) => {
                    if (stale) return;
                    setFound(r.users);
                    setSearchError(null);
                    setSearchedFor(text);
                    setSearching(false);
                })
                .catch((e) => {
                    if (stale) return;
                    setFound([]);
                    setSearchError(requestError(e, "La recherche a échoué"));
                    setSearchedFor(text);
                    setSearching(false);
                });
        }, 300);
        return () => {
            stale = true;
            clearTimeout(t);
        };
    }, [text]);

    // Typing a user's exact e-mail picks them, no click needed
    const exact = found.find((u) => u.email.toLowerCase() === text.toLowerCase());
    const target = selected || (exact ? { _id: String(exact._id), email: exact.email } : null);
    const searchDone = !searching && searchedFor === text && text.length >= 2;
    // A typed e-mail is sent as is (the server matches it exactly) unless the search already found no such account
    const byEmail = !target && EMAIL_RX.test(text) && !(searchDone && !searchError);
    const canCreate = (!!target || byEmail) && !!name.trim() && !creating;
    const blockedBy = !target && !byEmail ? "Choisissez d'abord un utilisateur" : !name.trim() ? "Donnez un nom à la clé" : undefined;

    const pick = (u: FoundUser) => setSelected({ _id: String(u._id), email: u.email });

    const create = async () => {
        if (!canCreate) return;
        setCreating(true);
        setError(null);
        const res = await adminCreateApiKey(target ? { userId: target._id } : { email: text }, name.trim());
        setCreating(false);
        if (res.error || !res.secret || !res.key) return setError(res.error || "Impossible de créer la clé");
        setCreated({ secret: res.secret, email: res.key.user?.email || target?.email || text, userId: String(res.key.userId), name: name.trim() });
        setCopied(false);
        setSelected(null);
        setQ("");
        setName("API");
        onCreated();
    };

    const copy = async () => {
        if (created && (await copyText(created.secret))) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-1">
                <Plus className="w-4 h-4 text-gray-500" />
                <p className="text-sm font-semibold text-gray-900">Créer une clé pour un client</p>
            </div>
            <p className="text-xs text-gray-500 mb-3">
                Les clients ne peuvent pas créer de clé eux-mêmes. L'accès API est actif dès la création (désactivable à tout moment dans « API publique »), et le client retrouve sa
                clé dans Paramètres → Accès API.
            </p>

            {created && (
                <div className="mb-3 border border-green-200 bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-800 mb-2">
                        Clé « {created.name} » créée pour <strong>{created.email}</strong>. Copiez-la pour la transmettre : elle reste aussi visible par le client dans ses paramètres.
                    </p>
                    <div className="flex gap-2">
                        <input readOnly value={created.secret} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 text-xs font-mono px-3 py-1.5 border border-green-200 rounded-lg bg-white" />
                        <button onClick={copy} className="text-xs px-3 py-1.5 rounded-lg bg-black text-white shrink-0">
                            {copied ? "Copiée !" : "Copier"}
                        </button>
                        <Link to={`/bo/public-api/${created.userId}`} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 shrink-0">
                            Voir le client
                        </Link>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                <div className="md:col-span-2">
                    <p className="text-xs text-gray-600 mb-1">Utilisateur</p>
                    {selected ? (
                        <div className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-1.5 text-xs">
                            <span className="text-gray-900 truncate">{selected.email}</span>
                            <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-gray-900 underline shrink-0">
                                Changer
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="relative">
                                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                <input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key !== "Enter") return;
                                        const only = exact || (searchDone && found.length === 1 ? found[0] : undefined);
                                        if (only) pick(only);
                                        else if (canCreate) create();
                                    }}
                                    placeholder="E-mail du client, ou recherche par entreprise, nom…"
                                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                                />
                            </div>
                            {text.length >= 2 && (
                                <p className={`mt-1 text-[11px] ${searchError ? "text-red-600" : exact ? "text-green-700" : "text-gray-500"}`}>
                                    {searchError
                                        ? `Recherche impossible : ${searchError}`
                                        : exact
                                          ? `La clé sera créée pour ${exact.email}.`
                                          : searching || !searchDone
                                            ? "Recherche…"
                                            : found.length === 0
                                              ? `Aucun utilisateur trouvé pour « ${text} ».`
                                              : "Cliquez sur un utilisateur pour le choisir, ou tapez son e-mail complet."}
                                </p>
                            )}
                            {found.length > 0 && (
                                <ul className="mt-1 border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-56 overflow-y-auto">
                                    {found.map((u) => (
                                        <li key={u._id}>
                                            <button
                                                onClick={() => pick(u)}
                                                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left hover:bg-[#F9F6F3] ${u === exact ? "bg-[#F9F6F3]" : ""}`}
                                            >
                                                <span className="min-w-0">
                                                    <span className="block text-gray-900 truncate">
                                                        {u === exact && <Check className="inline w-3.5 h-3.5 text-green-700 mr-1 -mt-0.5" />}
                                                        {u.email}
                                                    </span>
                                                    {(u.company || u.name) && <span className="block text-gray-500 truncate">{[u.name, u.company].filter(Boolean).join(" · ")}</span>}
                                                </span>
                                                <span className="shrink-0 text-[11px] text-gray-500">
                                                    {u.activeKeys ? `${u.activeKeys} clé${u.activeKeys > 1 ? "s" : ""} active${u.activeKeys > 1 ? "s" : ""}` : "aucune clé"}
                                                    {!u.enabled && <span className="ml-1 text-red-600">· accès désactivé</span>}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
                <div>
                    <p className="text-xs text-gray-600 mb-1">Nom de la clé</p>
                    <div className="flex gap-2">
                        <input
                            value={name}
                            maxLength={80}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && create()}
                            placeholder="ex. : CRM, Postman"
                            className="flex-1 min-w-0 text-xs px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                        />
                        <button onClick={create} disabled={!canCreate} title={blockedBy} className="text-xs px-3 py-1.5 rounded-lg bg-black text-white disabled:opacity-40 shrink-0">
                            {creating ? "Création…" : "Créer la clé"}
                        </button>
                    </div>
                    {blockedBy && !creating && <p className="mt-1 text-[11px] text-gray-400">{blockedBy}.</p>}
                </div>
            </div>
            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
    );
}

export default function ApiKeys() {
    const [keys, setKeys] = useState<AdminApiKey[]>([]);
    const [usageDays, setUsageDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<Status>("all");
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState<string | null>(null);

    const fetchKeys = async () => {
        setLoading(true);
        try {
            const res = await getAllApiKeys(status, q.trim() || undefined);
            setKeys(res.keys);
            setUsageDays(res.usageDays);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(fetchKeys, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [status, q]);

    const revoke = async (k: AdminApiKey) => {
        const reason = window.prompt(`Désactiver la clé « ${k.name} » de ${k.user?.email || "?"} ?\nRaison (facultatif) :`);
        if (reason === null) return;
        const res = await adminRevokeApiKey(k._id, reason);
        if (res.error) window.alert(res.error);
        fetchKeys();
    };

    const restore = async (k: AdminApiKey) => {
        if (!window.confirm(`Réactiver la clé « ${k.name} » de ${k.user?.email || "?"} ?`)) return;
        const res = await adminRestoreApiKey(k._id);
        if (res.error) window.alert(res.error);
        fetchKeys();
    };

    const active = keys.filter((k) => !k.revokedAt);
    const recentCalls = keys.reduce((s, k) => s + k.recentCalls, 0);
    const owners = new Set(active.map((k) => k.userId)).size;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">Clés API</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Clés API des clients : création (réservée à DuckFlow), usage, désactivation</p>
                </div>

                <BONavigation />

                <CreateKeyPanel onCreated={fetchKeys} />

                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Clés actives</p>
                        <p className="text-2xl font-semibold text-gray-900">{active.length}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Utilisateurs avec une clé active</p>
                        <p className="text-2xl font-semibold text-gray-900">{owners}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                        <p className="text-xs text-gray-500 mb-1">Appels enregistrés ({usageDays} j)</p>
                        <p className="text-2xl font-semibold text-gray-900">{recentCalls.toLocaleString("fr-FR")}</p>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-4">
                    <div className="flex gap-2">
                        {(["all", "active", "revoked"] as const).map((s) => (
                            <button
                                key={s}
                                onClick={() => setStatus(s)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    status === s ? "bg-[#1A1A1A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                }`}
                            >
                                {s === "all" ? "Toutes" : s === "active" ? "Actives" : "Désactivées"}
                            </button>
                        ))}
                    </div>
                    <div className="relative w-72">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="E-mail, entreprise, nom de clé…"
                            className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400"
                        />
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    {loading && keys.length === 0 ? (
                        <div className="flex items-center justify-center py-16 text-gray-400 text-sm">Chargement…</div>
                    ) : keys.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-2">
                            <KeyRound className="w-5 h-5" />
                            Aucune clé
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="w-8" />
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Utilisateur</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Clé</th>
                                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Appels (total)</th>
                                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">{usageDays} j</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Dernière utilisation</th>
                                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Statut</th>
                                    <th className="w-28" />
                                </tr>
                            </thead>
                            <tbody>
                                {keys.map((k) => {
                                    const open = expanded === k._id;
                                    return [
                                        <tr
                                            key={k._id}
                                            onClick={() => setExpanded(open ? null : k._id)}
                                            className={`border-b border-gray-50 hover:bg-[#F9F6F3] cursor-pointer ${k.revokedAt ? "opacity-60" : ""}`}
                                        >
                                            <td className="pl-3 text-gray-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                                            <td className="px-4 py-3">
                                                <p className="text-sm text-gray-900">{k.user?.email || "Utilisateur supprimé"}</p>
                                                {(k.user?.company || k.user?.name) && <p className="text-xs text-gray-500">{[k.user?.name, k.user?.company].filter(Boolean).join(" · ")}</p>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="text-sm text-gray-900">{k.name}</p>
                                                <p className="text-xs text-gray-400 font-mono">{k.prefix}… · créée {formatDate(k.createdAt)}</p>
                                            </td>
                                            <td className="px-4 py-3 text-right text-sm text-gray-900">{k.usageCount.toLocaleString("fr-FR")}</td>
                                            <td className="px-4 py-3 text-right text-sm text-gray-900">
                                                {k.recentCalls.toLocaleString("fr-FR")}
                                                {k.recentErrors > 0 && <span className="block text-[11px] text-red-600">{k.recentErrors} erreurs</span>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="text-xs text-gray-700">{formatDate(k.lastUsedAt)}</p>
                                                {k.lastUsedIp && <p className="text-[11px] text-gray-400 font-mono">{k.lastUsedIp}</p>}
                                            </td>
                                            <td className="px-4 py-3">
                                                {k.revokedAt ? (
                                                    <span
                                                        className="px-2 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-700 border border-red-100"
                                                        title={[k.revokedBy === "user" ? "Supprimée par l'utilisateur" : `Désactivée par ${k.revokedBy}`, k.revokeReason].filter(Boolean).join(" — ")}
                                                    >
                                                        {k.revokedBy === "user" ? "Supprimée" : "Désactivée"} · {formatDate(k.revokedAt)}
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-green-50 text-green-700 border border-green-100">Active</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                                {k.revokedAt ? (
                                                    <button onClick={() => restore(k)} className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                        Réactiver
                                                    </button>
                                                ) : (
                                                    <button onClick={() => revoke(k)} className="text-xs px-2.5 py-1 rounded-sm border border-red-200 text-red-600 hover:bg-red-50">
                                                        Désactiver
                                                    </button>
                                                )}
                                            </td>
                                        </tr>,
                                        open && (
                                            <tr key={`${k._id}-usage`}>
                                                <td colSpan={8} className="p-0">
                                                    <UsagePanel apiKey={k} />
                                                </td>
                                            </tr>
                                        ),
                                    ];
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
